import is from '@sindresorhus/is';
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '../../../../shared/source/context/context';
import {
    featuredLearningOpportunityUsersResponseSchema,
    userFeaturedLearningOpportunityOptionsSchema,
} from '../../../../shared/source/data-contract/featured-learning-opportunities';
import { userPaginationSchema } from '../../../../shared/source/data-contract/user';

import type { PagedBodyParams, SortedBodyParams } from '../../../../shared/source/data-contract/common';
import type {
    FeaturedLearningOpportunityUsersResponse,
    UserFeaturedLearningOpportunityOptions,
} from '../../../../shared/source/data-contract/featured-learning-opportunities';
import type { GeneralFilter } from '../../../../shared/source/data-contract/filter';
import type { Pathname } from '../../../../shared/source/http-fetcher/http-fetcher';
import type { UseQueryResult } from '@tanstack/react-query';

export const useFeaturedLearningOpportunityOptions: (
    userId: string,
) => UseQueryResult<UserFeaturedLearningOpportunityOptions> = (userId) => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/users/${userId}/featured-opportunities/options`;

    return useQuery({
        enabled: is.nonEmptyString(userId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(userFeaturedLearningOpportunityOptionsSchema, {
                pathname,
            });
            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET'],
    });
};

export const useFeaturedLearningOpportunityUsersFilterQuery = ({
    countOnly,
    enabled,
    filter,
    learningOpportunityId,
    paged,
    sorted,
}: {
    readonly countOnly?: boolean;
    readonly enabled?: boolean;
    readonly filter?: GeneralFilter;
    readonly learningOpportunityId: string;
    readonly paged?: PagedBodyParams;
    readonly sorted?: SortedBodyParams;
}): UseQueryResult<FeaturedLearningOpportunityUsersResponse> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: `/${string}` = `/featured-opportunities/${learningOpportunityId}/filter`;

    const payload =
        countOnly === true
            ? {
                  paged: {
                      countOnly: true,
                      offset: 0,
                      page: 1,
                      size: 1,
                  },
              }
            : {
                  ...(is.plainObject(paged) && { paged }),
                  ...(is.plainObject(filter) && { filter }),
                  ...(is.plainObject(sorted) && { sorted }),
                  ...(is.boolean(countOnly) && { countOnly }),
              };

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.postJson(
                featuredLearningOpportunityUsersResponseSchema,
                {
                    pathname,
                    payload,
                },
            );
            if (result.isOk) {
                return result.value;
            }
            throw result.error;
        },
        queryKey: [
            pathname,
            'postJson',
            paged,
            sorted,
            featuredLearningOpportunityUsersResponseSchema,
            validatingCobrainerApiFetcher,
            userPaginationSchema,
            payload,
        ],
    });
};
